import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import VideoCropper from "@/components/VideoCropper";
import ImageCropper from "@/components/ImageCropper";
import VideoTrimmer from "@/components/VideoTrimmer";
import { useTheme } from "@/context/ThemeContext";
import { 
  Heart, 
  MessageCircle, 
  Share2, 
  Image, 
  Video, 
  MapPin, 
  Trophy,
  Send,
  MoreHorizontal,
  Compass,
  Users,
  Plus,
  X,
  Loader2,
  Target,
  Play,
  Pause,
  Volume2,
  VolumeX,
  Scissors,
  Crop,
  Trash2,
  UserPlus,
  Ban,
  Flag,
  Bookmark,
  BookmarkCheck,
  ChevronLeft,
  ChevronRight,
  Clock,
  Building2,
  ArrowLeft
} from "lucide-react";
import StoryBar from "@/components/StoryBar";

const SocialFeedPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("discover");
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [creating, setCreating] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  
  // Create post state
  const [newPost, setNewPost] = useState({
    type: "text",
    content: "",
    mediaUrl: "",
    visibility: "public"
  });
  
  // Media editing states
  const [showVideoCropper, setShowVideoCropper] = useState(false);
  const [showImageCropper, setShowImageCropper] = useState(false);
  const [showVideoTrimmer, setShowVideoTrimmer] = useState(false);
  const [rawMediaUrl, setRawMediaUrl] = useState("");
  const [rawMediaType, setRawMediaType] = useState("");
  
  // Check-in state
  const [showCheckInDialog, setShowCheckInDialog] = useState(false);
  const [venues, setVenues] = useState([]);
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [checkInContent, setCheckInContent] = useState("");
  const [loadingVenues, setLoadingVenues] = useState(false);
  
  // Comment state
  const [commentDialogPost, setCommentDialogPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [loadingComments, setLoadingComments] = useState(false);
  const [submittingComment, setSubmittingComment] = useState(false);
  
  // Playing video state
  const [playingVideo, setPlayingVideo] = useState(null);
  const [mutedVideos, setMutedVideos] = useState({});
  
  // Saved posts
  const [savedPosts, setSavedPosts] = useState([]);
  
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchPosts();
    fetchSavedPosts();
  }, [activeTab]);

  const fetchPosts = async (append = false) => {
    try {
      if (append) {
        setLoadingMore(true);
      }
      const skip = append ? posts.length : 0;
      const response = await axios.get(`${API}/social/feed?tab=${activeTab}&skip=${skip}&limit=20`);
      
      if (append) {
        setPosts(prev => [...prev, ...response.data.posts]);
      } else {
        setPosts(response.data.posts || []);
      }
      setHasMore(response.data.hasMore);
    } catch (error) {
      console.error("Feed yüklenemedi:", error);
      if (!append) {
        setPosts([]);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const fetchSavedPosts = async () => {
    try {
      // Saved posts endpoint - şu an mock
      setSavedPosts([]);
    } catch (error) {
      console.error("Saved posts fetch error:", error);
    }
  };

  const fetchVenues = async () => {
    setLoadingVenues(true);
    try {
      const response = await axios.get(`${API}/venues`);
      setVenues(response.data || []);
    } catch (error) {
      console.error("Venues fetch error:", error);
      setVenues([]);
    } finally {
      setLoadingVenues(false);
    }
  };

  const handleCheckIn = async () => {
    if (!selectedVenue) {
      toast.error("Bir saha seçin");
      return;
    }
    
    setCreating(true);
    try {
      const response = await axios.post(`${API}/social/check-in/${selectedVenue.id}`, null, {
        params: { content: checkInContent || null }
      });
      if (response.data) {
        toast.success("Check-in yapıldı!");
        setPosts(prev => [response.data.post || response.data, ...prev]);
        setShowCheckInDialog(false);
        setSelectedVenue(null);
        setCheckInContent("");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Check-in yapılamadı");
    } finally {
      setCreating(false);
    }
  };

  const handleVideoTrimComplete = (trimmedVideo, trimInfo) => {
    // Video kırpıldıktan sonra direkt paylaşıma hazır
    setNewPost(prev => ({
      ...prev,
      type: 'video',
      mediaUrl: trimmedVideo
    }));
    setRawMediaUrl(trimmedVideo);
    setShowVideoTrimmer(false);
    // Crop adımını atla - video direkt paylaşılabilir
    // setShowVideoCropper(true);
  };

  const handleCreatePost = async () => {
    if (!newPost.content && !newPost.mediaUrl) {
      toast.error("Bir şeyler yazın veya medya ekleyin");
      return;
    }
    
    setCreating(true);
    try {
      const response = await axios.post(`${API}/social/posts`, newPost);
      if (response.data) {
        toast.success("Paylaşıldı!");
        setPosts(prev => [response.data, ...prev]);
        setShowCreatePost(false);
        setNewPost({ type: "text", content: "", mediaUrl: "", visibility: "public" });
        setRawMediaUrl("");
        setRawMediaType("");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Paylaşılamadı");
    } finally {
      setCreating(false);
    }
  };

  const handleLike = async (postId) => {
    try {
      const response = await axios.post(`${API}/social/posts/${postId}/like`);
      setPosts(prev => prev.map(post => 
        post.id === postId 
          ? { ...post, isLiked: response.data.liked, likeCount: response.data.likeCount }
          : post
      ));
    } catch (error) {
      toast.error("İşlem başarısız");
    }
  };

  const handleMediaSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Max boyut kontrolü: 5MB görsel, 50MB video
    const isVideo = file.type.startsWith('video');
    const maxSize = isVideo ? 50 * 1024 * 1024 : 5 * 1024 * 1024;
    
    if (file.size > maxSize) {
      toast.error(`Dosya çok büyük (max ${isVideo ? '50MB' : '5MB'})`);
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const mediaUrl = e.target.result;
      setRawMediaUrl(mediaUrl);
      setRawMediaType(isVideo ? 'video' : 'photo');
      
      if (isVideo) {
        // Video için direkt paylaşıma hazırla (düzenleme isteğe bağlı)
        setNewPost(prev => ({
          ...prev,
          type: 'video',
          mediaUrl: mediaUrl
        }));
        toast.success("Video eklendi! Düzenlemek için 'Videoyu Düzenle' butonuna tıklayın.");
      } else {
        // Görsel için cropper aç
        setShowImageCropper(true);
      }
    };
    reader.readAsDataURL(file);
    
    // Input'u temizle (aynı dosyayı tekrar seçebilmek için)
    e.target.value = '';
  };

  const handleCroppedMedia = (croppedMediaUrl) => {
    setNewPost(prev => ({
      ...prev,
      type: rawMediaType,
      mediaUrl: croppedMediaUrl
    }));
  };

  const handleDeletePost = async (postId) => {
    try {
      await axios.delete(`${API}/social/posts/${postId}`);
      setPosts(prev => prev.filter(p => p.id !== postId));
      toast.success("Paylaşım silindi");
    } catch (error) {
      toast.error("Silinemedi");
    }
  };

  const handleSavePost = async (postId) => {
    const isSaved = savedPosts.includes(postId);
    if (isSaved) {
      setSavedPosts(prev => prev.filter(id => id !== postId));
      toast.success("Kaydedilenlerden çıkarıldı");
    } else {
      setSavedPosts(prev => [...prev, postId]);
      toast.success("Kaydedildi");
    }
  };

  const openCommentDialog = async (post) => {
    setCommentDialogPost(post);
    setLoadingComments(true);
    try {
      const response = await axios.get(`${API}/social/posts/${post.id}/comments`);
      setComments(response.data?.comments || []);
    } catch (error) {
      console.error("Yorumlar yüklenemedi:", error);
      setComments([]);
    } finally {
      setLoadingComments(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !commentDialogPost) return;
    
    setSubmittingComment(true);
    try {
      const response = await axios.post(`${API}/social/posts/${commentDialogPost.id}/comments`, {
        content: newComment
      });
      
      setComments(prev => [...prev, response.data?.comment || response.data]);
      setNewComment("");
      
      // Post'un yorum sayısını güncelle
      setPosts(prev => prev.map(p => 
        p.id === commentDialogPost.id 
          ? { ...p, commentCount: (p.commentCount || 0) + 1 }
          : p
      ));
      
      toast.success("Yorum eklendi");
    } catch (error) {
      toast.error("Yorum eklenemedi");
    } finally {
      setSubmittingComment(false);
    }
  };

  const toggleVideoPlay = (postId) => {
    setPlayingVideo(prev => prev === postId ? null : postId);
  };

  const toggleVideoMute = (postId, e) => {
    e.stopPropagation();
    setMutedVideos(prev => ({
      ...prev,
      [postId]: !prev[postId]
    }));
  };

  const getPostTypeIcon = (type) => {
    switch (type) {
      case 'photo': return <Image className="h-4 w-4" />;
      case 'video': return <Video className="h-4 w-4" />;
      case 'check_in': return <MapPin className="h-4 w-4" />;
      case 'challenge': return <Target className="h-4 w-4" />;
      case 'match_result': return <Trophy className="h-4 w-4" />;
      default: return null;
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = (now - date) / 1000;
    
    if (diff < 60) return "Az önce";
    if (diff < 3600) return `${Math.floor(diff / 60)} dk`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} saat`;
    if (diff < 604800) return `${Math.floor(diff / 86400)} gün`;
    return date.toLocaleDateString('tr-TR');
  };

  const getPositionLabel = (position) => {
    const labels = {
      forward: "Forvet",
      midfielder: "Orta Saha",
      defender: "Defans",
      goalkeeper: "Kaleci"
    };
    return labels[position] || position;
  };

  // Dark mode flag - #1f2224 modern koyu tema
  const isDark = theme === 'dark';

  if (loading) {
    return (
      <div className="min-h-screen bg-background transition-colors duration-300">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background transition-colors duration-300">
      <Navbar user={user} onLogout={onLogout} />
      
      <div className="container mx-auto px-4 py-6 max-w-2xl">
        {/* Geri Butonu */}
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-4 text-gray-600 hover:text-gray-800 hover:bg-gray-100 dark:text-white dark:hover:text-white dark:hover:bg-white/10 -ml-2"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Geri
        </Button>

        {/* Story Bar */}
        <StoryBar user={user} onStoryCreated={() => fetchPosts()} isDark={isDark} />

        {/* Create Post Card */}
        <Card className={`mb-6 shadow-sm ${isDark ? 'bg-[#2a2d2f] border-[#3a3d40]' : ''}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Avatar className="h-10 w-10 cursor-pointer" onClick={() => navigate(`/profile/${user?.id}`)}>
                <AvatarImage src={user?.profileImage} />
                <AvatarFallback className="bg-emerald-600 text-white">{user?.name?.charAt(0)}</AvatarFallback>
              </Avatar>
              <button
                onClick={() => setShowCreatePost(true)}
                className={`flex-1 text-left px-4 py-2.5 rounded-full transition text-sm ${isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
              >
                Ne düşünüyorsun?
              </button>
            </div>
            <div className={`flex justify-around mt-3 pt-3 border-t ${isDark ? 'border-gray-600' : ''}`}>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => { 
                  setRawMediaType('photo');
                  setShowCreatePost(true); 
                  setTimeout(() => fileInputRef.current?.click(), 100);
                }}
                className="text-green-600 hover:text-green-700 hover:bg-green-50"
              >
                <Image className="h-4 w-4 mr-2" />
                Fotoğraf
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => { 
                  setRawMediaType('video');
                  setShowCreatePost(true); 
                  setTimeout(() => fileInputRef.current?.click(), 100);
                }}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <Video className="h-4 w-4 mr-2" />
                Video
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => {
                  fetchVenues();
                  setShowCheckInDialog(true);
                }}
                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
              >
                <MapPin className="h-4 w-4 mr-2" />
                Check-in
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList className={`grid w-full grid-cols-2 ${isDark ? 'bg-[#2a2d2f]' : ''}`}>
            <TabsTrigger 
              value="discover" 
              className={`gap-2 ${isDark ? 'data-[state=active]:bg-gray-700 data-[state=active]:text-white text-gray-300' : ''}`}
            >
              <Compass className="h-4 w-4" />
              Keşfet
            </TabsTrigger>
            <TabsTrigger 
              value="connections" 
              className={`gap-2 ${isDark ? 'data-[state=active]:bg-gray-700 data-[state=active]:text-white text-gray-300' : ''}`}
            >
              <Users className="h-4 w-4" />
              Bağlantılar
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Posts */}
        <div className="space-y-4">
          {posts.length === 0 ? (
            <Card className={`text-center py-12 ${isDark ? 'bg-[#2a2d2f] border-[#3a3d40]' : ''}`}>
              <CardContent>
                <Compass className={`h-12 w-12 mx-auto mb-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                <p className={`mb-2 ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                  {activeTab === "connections" 
                    ? "Bağlantılarınızdan henüz paylaşım yok" 
                    : "Henüz paylaşım yok"}
                </p>
                <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-gray-400'}`}>
                  {activeTab === "connections"
                    ? "Diğer oyuncularla bağlantı kurarak başlayın"
                    : "İlk paylaşımı yapan siz olun!"}
                </p>
                <Button onClick={() => setShowCreatePost(true)} className="bg-emerald-600 hover:bg-emerald-700">
                  <Plus className="h-4 w-4 mr-2" />
                  İlk Paylaşımı Yap
                </Button>
              </CardContent>
            </Card>
          ) : (
            posts.map((post) => (
              <Card key={post.id} className={`overflow-hidden shadow-sm ${isDark ? 'bg-[#2a2d2f] border-[#3a3d40]' : ''}`}>
                {/* Post Header */}
                <div className="p-4 pb-2">
                  <div className="flex items-start justify-between">
                    <div 
                      className="flex items-center gap-3 cursor-pointer"
                      onClick={() => navigate(`/profile/${post.userId}`)}
                    >
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={post.userImage} />
                        <AvatarFallback className="bg-emerald-600 text-white">{post.userName?.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className={`font-semibold text-sm hover:underline ${isDark ? 'text-white' : ''}`}>{post.userName}</p>
                          {post.userPosition && (
                            <Badge variant="outline" className={`text-xs ${isDark ? 'text-white border-white/30' : ''}`}>
                              {getPositionLabel(post.userPosition)}
                            </Badge>
                          )}
                        </div>
                        <div className={`flex items-center gap-2 text-xs ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                          <span>{formatTime(post.createdAt)}</span>
                          {getPostTypeIcon(post.type)}
                        </div>
                      </div>
                    </div>
                    
                    {/* Post Menu */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className={`h-8 w-8 p-0 ${isDark ? 'text-white hover:bg-white/10' : ''}`}>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleSavePost(post.id)}>
                          {savedPosts.includes(post.id) ? (
                            <>
                              <BookmarkCheck className="h-4 w-4 mr-2" />
                              Kaydedilenlerden Çıkar
                            </>
                          ) : (
                            <>
                              <Bookmark className="h-4 w-4 mr-2" />
                              Kaydet
                            </>
                          )}
                        </DropdownMenuItem>
                        {post.userId !== user?.id && (
                          <>
                            <DropdownMenuItem onClick={() => navigate(`/profile/${post.userId}`)}>
                              <UserPlus className="h-4 w-4 mr-2" />
                              Profili Gör
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem className="text-orange-600">
                              <Flag className="h-4 w-4 mr-2" />
                              Şikayet Et
                            </DropdownMenuItem>
                          </>
                        )}
                        {post.userId === user?.id && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              className="text-red-600"
                              onClick={() => handleDeletePost(post.id)}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Sil
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>

                {/* Post Content */}
                {post.content && (
                  <div className="px-4 pb-2">
                    <p className={`text-sm whitespace-pre-wrap ${isDark ? 'text-white' : ''}`}>{post.content}</p>
                  </div>
                )}

                {/* Check-in Badge */}
                {post.type === "check_in" && post.venueName && (
                  <div className="px-4 pb-2">
                    <Badge 
                      className={`cursor-pointer ${isDark ? 'bg-blue-900/50 text-blue-300 hover:bg-blue-800/50' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'}`}
                      onClick={() => post.venueId && navigate(`/venue/${post.venueId}/stories`)}
                    >
                      <MapPin className="h-3 w-3 mr-1" />
                      {post.venueName}
                    </Badge>
                  </div>
                )}

                {/* Match Result */}
                {post.type === "match_result" && (
                  <div className="px-4 pb-2">
                    <div className={`rounded-lg p-3 text-center ${isDark ? 'bg-emerald-900/30' : 'bg-emerald-50'}`}>
                      <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{post.matchTeams}</p>
                      <p className={`text-2xl font-bold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>{post.matchScore}</p>
                    </div>
                  </div>
                )}

                {/* Media */}
                {post.mediaUrl && (
                  <div className="relative bg-black">
                    {post.type === "video" ? (
                      <div className="relative" onClick={() => toggleVideoPlay(post.id)}>
                        <video 
                          src={post.mediaUrl} 
                          className="w-full max-h-[500px] object-contain cursor-pointer"
                          playsInline
                          loop
                          muted={mutedVideos[post.id] !== false}
                          autoPlay={playingVideo === post.id}
                          onPlay={() => setPlayingVideo(post.id)}
                          onPause={() => setPlayingVideo(prev => prev === post.id ? null : prev)}
                        />
                        {/* Video Controls Overlay */}
                        <div className="absolute bottom-3 right-3 flex gap-2">
                          <Button 
                            size="sm" 
                            variant="secondary" 
                            className="h-8 w-8 p-0 bg-black/50 hover:bg-black/70 text-white"
                            onClick={(e) => toggleVideoMute(post.id, e)}
                          >
                            {mutedVideos[post.id] === false ? (
                              <Volume2 className="h-4 w-4" />
                            ) : (
                              <VolumeX className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                        {/* Play/Pause Indicator */}
                        {playingVideo !== post.id && (
                          <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                            <div className="bg-black/50 rounded-full p-4">
                              <Play className="h-8 w-8 text-white" />
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <img 
                        src={post.mediaUrl} 
                        alt="Post" 
                        className="w-full max-h-[500px] object-contain"
                      />
                    )}
                  </div>
                )}

                {/* Post Actions */}
                <div className="p-4 pt-2">
                  <div className={`flex items-center justify-between text-sm mb-2 ${isDark ? 'text-white' : 'text-gray-500'}`}>
                    <span>{post.likeCount || 0} beğeni</span>
                    <span>{post.commentCount || 0} yorum</span>
                  </div>
                  <div className={`flex justify-around pt-2 border-t ${isDark ? 'border-gray-600' : ''}`}>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className={post.isLiked ? "text-red-500 hover:text-red-600" : `${isDark ? 'text-white' : ''} hover:text-red-500`}
                      onClick={() => handleLike(post.id)}
                    >
                      <Heart className={`h-4 w-4 mr-2 ${post.isLiked ? "fill-current" : ""}`} />
                      Beğen
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => openCommentDialog(post)}
                      className={`${isDark ? 'text-white' : ''} hover:text-blue-500`}
                    >
                      <MessageCircle className="h-4 w-4 mr-2" />
                      Yorum
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className={`${isDark ? 'text-white' : ''} hover:text-green-500`}
                      onClick={() => {
                        navigator.clipboard.writeText(window.location.origin + `/post/${post.id}`);
                        toast.success("Link kopyalandı");
                      }}
                    >
                      <Share2 className="h-4 w-4 mr-2" />
                      Paylaş
                    </Button>
                  </div>
                </div>
              </Card>
            ))
          )}

          {/* Load More */}
          {hasMore && posts.length > 0 && (
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => fetchPosts(true)}
              disabled={loadingMore}
            >
              {loadingMore ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Daha Fazla Göster
            </Button>
          )}
        </div>
      </div>

      {/* Create Post Dialog */}
      <Dialog open={showCreatePost} onOpenChange={(open) => {
        setShowCreatePost(open);
        if (!open) {
          setNewPost({ type: "text", content: "", mediaUrl: "", visibility: "public" });
          setRawMediaUrl("");
          setRawMediaType("");
        }
      }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Paylaşım Oluştur</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Avatar>
                <AvatarImage src={user?.profileImage} />
                <AvatarFallback className="bg-emerald-600 text-white">{user?.name?.charAt(0)}</AvatarFallback>
              </Avatar>
              <div>
                <p className="font-semibold">{user?.name}</p>
                <select 
                  className="text-xs text-gray-500 bg-transparent border-none p-0 cursor-pointer"
                  value={newPost.visibility}
                  onChange={(e) => setNewPost(p => ({...p, visibility: e.target.value}))}
                >
                  <option value="public">🌍 Herkese Açık</option>
                  <option value="connections">👥 Bağlantılar</option>
                  <option value="private">🔒 Sadece Ben</option>
                </select>
              </div>
            </div>
            
            <Textarea
              placeholder="Ne düşünüyorsun?"
              value={newPost.content}
              onChange={(e) => setNewPost(p => ({...p, content: e.target.value}))}
              rows={4}
              className="resize-none"
            />
            
            {/* Media Preview */}
            {newPost.mediaUrl && (
              <div className="relative rounded-lg overflow-hidden bg-gray-100">
                {newPost.type === "video" ? (
                  <video 
                    src={newPost.mediaUrl} 
                    className="w-full max-h-60 object-contain" 
                    controls 
                    playsInline
                  />
                ) : (
                  <img 
                    src={newPost.mediaUrl} 
                    alt="Preview" 
                    className="w-full max-h-60 object-contain" 
                  />
                )}
                <div className="absolute top-2 right-2 flex gap-2">
                  <Button 
                    size="sm" 
                    variant="secondary"
                    className="h-8 w-8 p-0 bg-black/50 hover:bg-black/70 text-white"
                    onClick={() => {
                      setRawMediaUrl(newPost.mediaUrl);
                      if (newPost.type === "video") {
                        setShowVideoTrimmer(true);
                      } else {
                        setShowImageCropper(true);
                      }
                    }}
                    title={newPost.type === "video" ? "Videoyu Kes" : "Görseli Düzenle"}
                  >
                    <Scissors className="h-4 w-4" />
                  </Button>
                  <Button 
                    size="sm" 
                    variant="destructive"
                    className="h-8 w-8 p-0"
                    onClick={() => {
                      setNewPost(p => ({...p, mediaUrl: "", type: "text"}));
                      setRawMediaUrl("");
                      setRawMediaType("");
                    }}
                    title="Kaldır"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
            
            {/* Media Buttons */}
            <div className="flex gap-2">
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                accept="image/*,video/*"
                onChange={handleMediaSelect}
              />
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                className="flex-1"
              >
                <Image className="h-4 w-4 mr-2 text-green-500" />
                Fotoğraf
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                className="flex-1"
              >
                <Video className="h-4 w-4 mr-2 text-red-500" />
                Video
              </Button>
            </div>
            
            {/* Tip */}
            <p className="text-xs text-gray-400 text-center">
              Video max 50MB, Görsel max 5MB • Kırpma ve orantılama yapabilirsiniz
            </p>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreatePost(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleCreatePost} 
              disabled={creating || (!newPost.content && !newPost.mediaUrl)}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {creating ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Paylaş
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Comment Dialog */}
      <Dialog open={!!commentDialogPost} onOpenChange={(open) => !open && setCommentDialogPost(null)}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Yorumlar</DialogTitle>
          </DialogHeader>
          
          {/* Post Preview */}
          {commentDialogPost && (
            <div className="border-b pb-3 mb-3">
              <div className="flex items-center gap-2 mb-2">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={commentDialogPost.userImage} />
                  <AvatarFallback>{commentDialogPost.userName?.charAt(0)}</AvatarFallback>
                </Avatar>
                <span className="font-medium text-sm">{commentDialogPost.userName}</span>
              </div>
              {commentDialogPost.content && (
                <p className="text-sm text-gray-700 line-clamp-2">{commentDialogPost.content}</p>
              )}
            </div>
          )}
          
          {/* Comments List */}
          <div className="flex-1 overflow-y-auto space-y-3 min-h-[200px]">
            {loadingComments ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            ) : comments.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <MessageCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Henüz yorum yok</p>
                <p className="text-xs">İlk yorumu sen yap!</p>
              </div>
            ) : (
              comments.map((comment) => (
                <div key={comment.id} className="flex gap-2">
                  <Avatar 
                    className="h-8 w-8 cursor-pointer" 
                    onClick={() => navigate(`/profile/${comment.userId}`)}
                  >
                    <AvatarImage src={comment.userImage} />
                    <AvatarFallback>{comment.userName?.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <div className="bg-gray-100 rounded-lg px-3 py-2">
                      <p className="font-medium text-sm">{comment.userName}</p>
                      <p className="text-sm text-gray-700">{comment.content}</p>
                    </div>
                    <p className="text-xs text-gray-400 mt-1 ml-2">
                      {formatTime(comment.createdAt)}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
          
          {/* Add Comment */}
          <div className="flex gap-2 pt-3 border-t">
            <Avatar className="h-8 w-8">
              <AvatarImage src={user?.profileImage} />
              <AvatarFallback>{user?.name?.charAt(0)}</AvatarFallback>
            </Avatar>
            <div className="flex-1 flex gap-2">
              <input
                type="text"
                placeholder="Yorum yaz..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleAddComment()}
                className="flex-1 px-3 py-2 text-sm border rounded-full focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
              <Button 
                size="sm" 
                onClick={handleAddComment}
                disabled={!newComment.trim() || submittingComment}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {submittingComment ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Video Cropper Dialog */}
      <VideoCropper
        open={showVideoCropper}
        onClose={() => setShowVideoCropper(false)}
        videoSrc={rawMediaUrl}
        onCropComplete={handleCroppedMedia}
      />

      {/* Image Cropper Dialog */}
      <ImageCropper
        open={showImageCropper}
        onClose={() => setShowImageCropper(false)}
        imageSrc={rawMediaUrl}
        onCropComplete={handleCroppedMedia}
      />

      {/* Video Trimmer Dialog */}
      <VideoTrimmer
        open={showVideoTrimmer}
        onClose={() => setShowVideoTrimmer(false)}
        videoSrc={rawMediaUrl}
        onTrimComplete={handleVideoTrimComplete}
        maxDuration={60}
      />

      {/* Check-in Dialog */}
      <Dialog open={showCheckInDialog} onOpenChange={setShowCheckInDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-blue-600" />
              Saha Check-in
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {loadingVenues ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              </div>
            ) : venues.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Building2 className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>Henüz saha bulunamadı</p>
              </div>
            ) : (
              <div className="max-h-60 overflow-y-auto space-y-2">
                {venues.map((venue) => (
                  <div
                    key={venue.id}
                    onClick={() => setSelectedVenue(venue)}
                    className={`p-3 rounded-lg border cursor-pointer transition ${
                      selectedVenue?.id === venue.id 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded bg-blue-100 flex items-center justify-center">
                        <MapPin className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium">{venue.name}</p>
                        <p className="text-sm text-gray-500">{venue.district}, {venue.city}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {selectedVenue && (
              <Textarea
                placeholder="Bir şeyler yaz... (opsiyonel)"
                value={checkInContent}
                onChange={(e) => setCheckInContent(e.target.value)}
                rows={2}
              />
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCheckInDialog(false)}>
              İptal
            </Button>
            <Button
              onClick={handleCheckIn}
              disabled={!selectedVenue || creating}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {creating ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <MapPin className="h-4 w-4 mr-2" />
              )}
              Check-in Yap
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SocialFeedPage;
